function nasqaRoot() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

const dialogOpeners = new WeakMap();

/* ---------------- predictive search ---------------- */
class NasqaPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.controller = null;
    this.timer = null;
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 180);
    });
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.results.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    // If suggestions are unavailable on this storefront, stop hammering the endpoint.
    if (this.suggestUnavailable) { this.renderFallback(query); return; }
    this.results.setAttribute('aria-busy', 'true');
    const base = this.dataset.predictiveUrl || `${nasqaRoot()}search/suggest`;
    try {
      const url = `${base}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) {
        if (response.status === 417 || response.status === 404) this.suggestUnavailable = true;
        throw new Error(`Search request failed: ${response.status}`);
      }
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.querySelector('[data-predictive-results]');
      this.results.innerHTML = next?.innerHTML || '';
      this.input.setAttribute('aria-expanded', this.results.textContent.trim() ? 'true' : 'false');
    } catch (error) {
      if (error.name !== 'AbortError') this.renderFallback(query);
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  // If suggestions are unavailable, never leave the user with a dead dropdown.
  renderFallback(query) {
    this.results.replaceChildren();
    const link = document.createElement('a');
    link.className = 'text-link';
    link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}`;
    link.textContent = this.dataset.viewAll || '';
    const wrap = document.createElement('p');
    wrap.style.padding = '.75rem';
    wrap.append(link);
    this.results.append(wrap);
    this.input.setAttribute('aria-expanded', 'true');
  }
}

/* ---------------- add to cart ---------------- */
class NasqaProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submit = this.querySelector('[type="submit"]');
    // Bind even when the button starts disabled — it unlocks once a size is chosen.
    if (!this.form || !this.submit) return;
    this.form.addEventListener('submit', (event) => this.add(event));
  }

  async add(event) {
    event.preventDefault();
    if (!this.form.querySelector('[name="id"]:checked') && !this.form.querySelector('input[type="hidden"][name="id"]')) return;
    this.submit.setAttribute('aria-busy', 'true');
    this.submit.disabled = true;
    try {
      const response = await fetch(`${nasqaRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      });
      if (!response.ok) throw new Error((await response.json()).description || 'Unable to add item');
      await window.nasqaCart.refresh();
      window.nasqaCart.open();
      this.announce(this.dataset.successMessage || '');
      /* Success is a fact, not a guess. motion.js used to paint the button
         green on click, before this request had resolved — and it painted it
         green when the add failed too. It now listens for this. */
      this.submit.dispatchEvent(new CustomEvent('nasqa:add-success', { bubbles: true }));
    } catch (error) {
      this.announce(error.message);
    } finally {
      this.submit.removeAttribute('aria-busy');
      this.submit.disabled = false;
    }
  }

  announce(message) {
    const region = document.querySelector('[data-live-region]');
    if (region) region.textContent = message;
  }
}

/* ---------------- cart drawer ---------------- */
class NasqaCart {
  constructor() {
    this.dialog = document.querySelector('[data-cart-drawer]');
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    });
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    if (opener) dialogOpeners.set(this.dialog, opener);
    this.dialog.showModal();
  }

  async refresh() {
    if (!this.dialog) return;
    const response = await fetch(`${nasqaRoot()}?section_id=cart-drawer-content`);
    if (response.ok) {
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.querySelector('[data-cart-drawer-content]');
      const current = this.dialog.querySelector('[data-cart-drawer-content]');
      if (next && current) current.replaceWith(next);
    }
    const cart = await (await fetch(`${nasqaRoot()}cart.js`)).json();
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
  }
}

/* ---------------- product gallery ---------------- */
class NasqaGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (this.slides.length < 1) return;
    this.index = 0;

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.show(i));
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.index + 1));

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
      event.preventDefault();
      this.show(this.index + (forward ? 1 : -1));
    });

    this.show(0);
  }

  show(next) {
    const total = this.slides.length;
    this.index = (next + total) % total;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.index)));
    const active = this.thumbs[this.index];
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

customElements.define('nasqa-predictive-search', NasqaPredictiveSearch);
customElements.define('nasqa-product-form', NasqaProductForm);
customElements.define('nasqa-gallery', NasqaGallery);
window.nasqaCart = new NasqaCart();

/* ---------------- dialogs ---------------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog instanceof HTMLDialogElement) {
      dialogOpeners.set(dialog, opener);
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('input[type="search"]')?.focus();
    }
    return;
  }
  const close = event.target.closest('[data-dialog-close]');
  if (close) close.closest('dialog')?.close();
});

document.querySelectorAll('dialog').forEach((dialog) => {
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    dialog.close();
  });
  dialog.addEventListener('close', () => {
    const opener = dialogOpeners.get(dialog);
    if (opener?.isConnected) opener.focus();
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
});

/* ---------------- variants, stock, quantity, sticky ATC ---------------- */
(function productControls() {
  const options = [...document.querySelectorAll('[data-variant-option]')];
  const stockNote = document.querySelector('[data-stock-note]');
  const lowThreshold = Number(document.body.dataset.lowStock || 6);

  const syncVariant = (input) => {
    if (!input) return;
    document.querySelectorAll('[data-product-price]').forEach((el) => {
      if (input.dataset.price) el.textContent = input.dataset.price;
    });
    const available = input.dataset.available === 'true';
    document.querySelectorAll('[data-atc]').forEach((btn) => {
      btn.disabled = !available;
      if (btn.dataset.labelAdd) btn.textContent = btn.dataset.labelAdd;
    });

    if (!stockNote) return;
    const qty = Number(input.dataset.inventory || 0);
    if (!available || qty <= 0) {
      stockNote.dataset.level = 'out';
      stockNote.textContent = stockNote.dataset.outText || '';
    } else if (qty <= lowThreshold) {
      stockNote.dataset.level = 'low';
      stockNote.textContent = (stockNote.dataset.lowText || '').replace('%COUNT%', qty);
    } else {
      stockNote.dataset.level = 'in';
      stockNote.textContent = stockNote.dataset.inText || '';
    }
  };

  options.forEach((input) => input.addEventListener('change', () => syncVariant(input)));
  syncVariant(options.find((i) => i.checked) || options.find((i) => i.dataset.available === 'true'));

  // quantity stepper
  const qtyInput = document.querySelector('[data-qty-input]');
  if (qtyInput) {
    const step = (delta) => {
      const next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
      qtyInput.value = next;
    };
    document.querySelector('[data-qty-minus]')?.addEventListener('click', () => step(-1));
    document.querySelector('[data-qty-plus]')?.addEventListener('click', () => step(1));
  }

  // Sticky add-to-cart appears once the main button has scrolled above the viewport.
  // A scroll check rather than IntersectionObserver: a fast or programmatic jump can
  // skip the element entirely, so the observer never reports a threshold crossing.
  const sticky = document.querySelector('[data-sticky-atc]');
  const mainForm = document.getElementById('ProductForm');
  const anchor = mainForm && mainForm.querySelector('[data-atc]');
  if (sticky && anchor) {
    let frame = 0;
    const update = () => {
      frame = 0;
      const show = anchor.getBoundingClientRect().bottom < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
})();

/* ---------------- mobile menu ---------------- */
(function mobileMenu() {
  const menu = document.querySelector('[data-mobile-menu]');
  if (!menu) return;
  const panel = menu.querySelector('.mobile-menu__panel');
  let lastFocused = null;

  const open = (trigger) => {
    lastFocused = trigger || document.activeElement;
    menu.classList.add('is-open');
    menu.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    panel?.querySelector('a, button')?.focus();
  };
  const close = () => {
    menu.classList.remove('is-open');
    menu.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (lastFocused?.isConnected) lastFocused.focus();
  };

  menu.setAttribute('inert', '');
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mobile-menu-open]');
    if (trigger) { event.preventDefault(); open(trigger); return; }
    if (event.target.closest('[data-mobile-menu-close]') || event.target.closest('.mobile-menu__scrim')) close();
    if (event.target.closest('.mobile-menu__panel a')) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.classList.contains('is-open')) close();
  });
})();

/* ---------------- reveal on scroll ---------------- */
(function reveal() {
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Only animate what starts below the fold — above-the-fold content must paint immediately.
  const items = [...document.querySelectorAll('.reveal')].filter((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return false;
    el.classList.add('will-reveal');
    return true;
  });
  if (!items.length) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8%', threshold: 0.05 });
  items.forEach((item) => observer.observe(item));
})();

/* ---------------- back to top ---------------- */
(function backToTop() {
  const button = document.querySelector('[data-back-to-top]');
  if (!button) return;
  const toggle = () => button.classList.toggle('is-visible', window.scrollY > 700);
  window.addEventListener('scroll', toggle, { passive: true });
  button.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  toggle();
})();
